import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import ContactSection from "../../components/ContactSection";

const CollaborationServices = () => {

    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            id: '01',
            title: 'Call Control & Unified Communications',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.18 6.18l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'Solaris delivers enterprise-grade call control solutions that simplify and secure your voice infrastructure. We design, implement, and manage on-premises and cloud-based call management systems - ensuring seamless internal and external communication across your entire organisation.',
            accent: '#2563eb'
        },
        {
            id: '02',
            title: 'Transformative Video Conferencing',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><polygon points="23 7 16 12 23 17 23 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="2" /></svg>
            ),
            description: 'Solaris enables high-performance video conferencing ecosystems that connect teams, customers, and partners without friction. We deploy and integrate personal endpoints, room systems, and immersive meeting spaces to deliver consistent, high-quality collaboration experiences across every location.',
            accent: '#0ea5e9'
        },
        {
            id: '03',
            title: 'Contact Center Solutions',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'Solaris designs and integrates intelligent contact center solutions that elevate customer experience and operational efficiency. We implement cloud and on-premises contact center platforms with advanced routing, analytics, and workforce optimisation capabilities - enabling faster resolution, improved service quality, and measurable business outcomes.',
            accent: '#6366f1'
        },
        {
            id: '04',
            title: 'Collaboration Bots & Automation',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="16" r="1" fill="currentColor" /></svg>
            ),
            description: 'Supercharge your collaboration platforms with intelligent bots and workflow automation. Solaris develops and deploys custom bots, AI-driven assistants, and automated workflows that streamline routine tasks, accelerate decision-making, and enhance team productivity across messaging and meeting platforms.',
            accent: '#f59e0b'
        },
        {
            id: '05',
            title: 'Webinars & Webcasting Solutions',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2" /><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'Deliver impactful virtual events, product launches, and large-scale webcasts with Solaris-managed webinar solutions. We provide end-to-end event production including platform setup, attendee management, live streaming, interactive Q&A, and post-event analytics - ensuring a seamless and professional audience experience.',
            accent: '#10b981'
        },
        {
            id: '06',
            title: 'Messaging & Presence Platforms',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" /><path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'Unify your workforce communication with enterprise messaging and presence platforms. Solaris deploys, integrates, and manages solutions that provide real-time messaging, file sharing, presence indicators, and persistent workspaces - enabling your teams to collaborate efficiently regardless of location or device.',
            accent: '#ef4444'
        }
    ];

    const servicesSpectrum = [
        'Collaboration Security & Compliance',
        'Unified Communications & IP Telephony',
        'Cloud & Hybrid Calling Solutions',
        'Video Conferencing & Meeting Rooms',
        'Contact Center & CX Platforms',
        'Collaboration Bots & Automation',
        'Webinars & Webcasting Solutions',
        'Messaging & Presence Platforms',
        'IVR & Voice Portals',
        'Managed Collaboration Services'
    ];

    const caseStudies = [
        {
            tag: 'Collaboration Services',
            title: 'Unified Communications Transformation for Global Enterprise',
            image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Collaboration Services',
            title: 'Contact Center Modernisation Driving 40% Faster Resolution',
            image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&auto=format&fit=crop&q=80',
            dark: false
        },
        {
            tag: 'Collaboration Services',
            title: 'Cloud Calling Rollout Across 30+ International Offices',
            image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Collaboration Services',
            title: 'Video Collaboration Platform Deployed for 8,000+ Users',
            image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&auto=format&fit=crop&q=80',
            dark: false
        }
    ];

    const faqs = [
        {
            question: 'What collaboration platforms does Solaris support?',
            answer: 'Solaris supports a wide range of collaboration platforms including Cisco Webex, Microsoft Teams, Zoom, Avaya, Genesys, RingCentral, and Mitel. Our vendor-agnostic approach ensures we recommend the platform best suited to your workforce size, industry requirements, and existing IT infrastructure.'
        },
        {
            question: 'Can you integrate collaboration systems with existing IT infrastructure?',
            answer: 'Yes. Solaris specialises in integrating collaboration platforms with existing on-premises and cloud infrastructure, including Active Directory, Microsoft 365, CRM systems, and ERP platforms. We ensure seamless single sign-on, directory synchronisation, and unified identity management across all collaboration tools.'
        },
        {
            question: 'Do you provide managed services for collaboration platforms?',
            answer: 'Absolutely. Our managed collaboration services include 24/7 platform monitoring, proactive fault management, user provisioning, software updates, and end-user support. We offer fully managed and co-managed models with clearly defined SLAs to ensure your collaboration environment performs reliably at all times.'
        },
        {
            question: 'Are your collaboration solutions secure and compliant?',
            answer: 'Security and compliance are embedded in every collaboration deployment. Solaris applies encryption in transit and at rest, enforces access controls, enables call recording compliance, and aligns deployments with regulatory requirements including GDPR, ISO 27001, and industry-specific frameworks.'
        },
        {
            question: 'Can you support hybrid and remote working environments?',
            answer: 'Yes. Solaris designs collaboration architectures purpose-built for hybrid and distributed workforces. We deploy cloud calling, secure remote access, optimised meeting room solutions, and mobile-first communication tools - ensuring every employee has the same high-quality experience whether in the office, at home, or on the move.'
        },
        {
            question: 'Do you provide contact center and customer experience solutions?',
            answer: 'Solaris delivers end-to-end contact center solutions including cloud-based CCaaS platforms, IVR design, intelligent routing, workforce management, real-time analytics, and AI-powered virtual agents. We help organisations transform customer experience while reducing operational costs and improving agent productivity.'
        }
    ];

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{
                position: 'relative',
                width: '100%',
                minHeight: '480px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,20,50,0.72)' }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '120px 15px 80px', textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 style={{
                            color: '#fff', fontWeight: 700,
                            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                            lineHeight: 1.2, margin: '0 auto 20px',
                            maxWidth: '820px'
                        }}>
                            Enterprise Collaboration Solutions for a Connected, Productive Future
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.78)', fontSize: '1rem',
                            lineHeight: 1.75, maxWidth: '520px', margin: '0 auto 36px'
                        }}>
                            Build connected, agile, and highly productive workplaces through integrated communication and collaboration platforms designed for the modern enterprise
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <a href="#collab-overview" style={{ textDecoration: 'none' }}>
                                <button style={{
                                    display: 'inline-flex', alignItems: 'center',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    color: '#fff', border: 'none',
                                    padding: '14px 8px 14px 28px',
                                    borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                    cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: '0 8px 24px rgba(37,99,235,0.4)'
                                }}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                                >
                                    <span style={{ marginRight: '16px' }}>Know More</span>
                                    <span style={{
                                        background: '#fff', borderRadius: '50%',
                                        width: '38px', height: '38px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── WHAT IS COLLABORATION ── */}
            <section id="collab-overview" style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5">
                        {/* Left: image */}
                        <div className="col-lg-6">
                            <div style={{ position: 'relative' }}>
                                <img
                                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop&q=80"
                                    alt="Collaboration Team"
                                    style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', height: '400px', display: 'block' }}
                                />
                            </div>
                        </div>

                        {/* Right: text */}
                        <div className="col-lg-6">
                            <p style={{
                                color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                ENTERPRISE COLLABORATION, SIMPLIFIED
                            </p>
                            <h2 style={{
                                fontSize: 'clamp(1.9rem, 3vw, 2rem)', fontWeight: 700,
                                color: '#0f172a', lineHeight: 1.25, marginBottom: '28px', textAlign: 'left'
                            }}>
                                What is Collaboration?
                            </h2>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '20px', textAlign: 'left' }}>
                                Solaris's Collaboration Services enable organisations to build connected, agile, and highly productive workplaces through integrated communication and collaboration platforms. We design, implement, and manage advanced voice, video, messaging, and contact center solutions that support modern hybrid and remote work environments.
                            </p>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '32px', textAlign: 'left' }}>
                                Our solutions combine <strong style={{ color: '#0f172a' }}>unified communications, video conferencing, contact center technologies, and intelligent automation</strong> to deliver seamless engagement across internal teams and external stakeholders - driving operational efficiency and workforce effectiveness.
                            </p>
                            <Link to="/contact" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)', color: '#fff',
                                padding: '13px 28px', borderRadius: '8px', fontWeight: 700,
                                fontSize: '0.9rem', textDecoration: 'none',
                                boxShadow: '0 6px 20px rgba(37,99,235,0.3)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.45)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.3)'; }}
                            >
                                Discuss Your Collaboration Project
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SOLUTIONS GRID ── */}
            <section style={{ padding: '110px 0', background: '#0b1120', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', top: '-120px', left: '-120px',
                    width: '500px', height: '500px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-100px', right: '-100px',
                    width: '440px', height: '440px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Core Offerings
                            </span>
                        </div>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 3vw, 2rem)', fontWeight: 600,
                            color: '#fff', lineHeight: 1.2, marginBottom: '20px'
                        }}>
                            Connected Workplaces.{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Empowered Teams.
                            </span>
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
                            End-to-end collaboration expertise across every layer - from voice and video to contact centres, automation, and beyond.
                        </p>
                    </div>

                    <div className="row g-4">
                        {features.map((f, i) => (
                            <div key={f.id} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6 }}
                                    style={{
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.09)',
                                        borderRadius: '16px', padding: '36px 30px',
                                        height: '100%', cursor: 'default',
                                        transition: 'border-color 0.3s, background 0.3s',
                                        position: 'relative', overflow: 'hidden'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = f.accent + '55'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 0, left: '30px', right: '30px',
                                        height: '2px', borderRadius: '0 0 4px 4px',
                                        background: `linear-gradient(90deg, ${f.accent}, transparent)`
                                    }} />
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: f.accent + '20', border: `1px solid ${f.accent}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: f.accent, marginBottom: '22px'
                                    }}>
                                        {f.icon}
                                    </div>
                                    <div style={{
                                        fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)',
                                        letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px'
                                    }}>{f.id}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '14px', lineHeight: 1.4 }}>{f.title}</h3>
                                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: 0 }}>{f.description}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── UNIFIED ECOSYSTEM (Dark Section) ── */}
            <section style={{ position: 'relative', overflow: 'hidden', background: '#0f172a', padding: '100px 0' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&auto=format&fit=crop&q=80')`,
                    backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.18
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        <div className="col-lg-5">
                            <p style={{
                                color: '#38bdf8', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                SERVICES SPECTRUM
                            </p>
                            <h2 style={{
                                color: '#fff', fontWeight: 800,
                                fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
                                lineHeight: 1.25, marginBottom: '20px'
                            }}>
                                A Unified Collaboration Ecosystem
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.85 }}>
                                Comprehensive platforms and services designed to connect teams, customers, and systems securely and efficiently - from strategy through to ongoing management.
                            </p>
                        </div>
                        <div className="col-lg-7">
                            <div className="row g-3">
                                {servicesSpectrum.map((svc, i) => (
                                    <div key={i} className="col-md-6">
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: i * 0.07 }}
                                            viewport={{ once: true }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                padding: '16px 20px', borderRadius: '10px',
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseOver={e => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
                                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M5 13L9 17L19 7" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', fontWeight: 500 }}>{svc}</span>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY CHOOSE SOLARIS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            WHY SOLARIS
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '18px' }}>
                            Trusted Across Industries
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto' }}>
                            Delivering mission-critical collaboration infrastructure with precision, expertise, and a people-first approach.
                        </p>
                    </div>

                    <div className="row g-4">
                        {[
                            { icon: '🏗️', title: 'Design-Led Approach', desc: 'Every deployment starts with a comprehensive discovery and custom collaboration architecture - tailored to your workforce, not a one-size-fits-all template.' },
                            { icon: '🔒', title: 'Security & Compliance Built In', desc: 'Security is never bolted on. Encryption, access controls, call recording compliance, and regulatory alignment are embedded from day one.' },
                            { icon: '🤝', title: 'Vendor-Agnostic Expertise', desc: 'Elite partnerships with Cisco, Microsoft, Zoom, Avaya, Genesys, and more - we recommend what works best for your specific needs.' },
                            { icon: '⏱️', title: '24/7 Managed Support', desc: 'Round-the-clock monitoring, proactive fault management, and dedicated incident response - keeping your collaboration environment always available.' },
                            { icon: '⚡', title: 'Rapid Deployment', desc: 'Proven project methodologies and certified engineers ensure on-time, on-budget delivery - from single-site rollouts to global UC transformations.' },
                            { icon: '📊', title: 'Data-Driven Optimisation', desc: 'Continuous performance analytics, usage reporting, and AI-driven insights keep your collaboration platforms tuned for peak productivity.' }
                        ].map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '32px 28px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default'
                                    }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)', borderColor: '#93c5fd' }}
                                >
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '14px',
                                        background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.6rem', marginBottom: '20px'
                                    }}>{item.icon}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: 0 }}>{item.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CASE STUDIES ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            CASE STUDIES
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '0' }}>
                            Driving Business Impact<br />Through Collaboration
                        </h2>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={24}
                            slidesPerView={1}
                            navigation={{ prevEl: '.cs-cl-prev-btn', nextEl: '.cs-cl-next-btn' }}
                            pagination={{ clickable: true, el: '.cs-cl-pagination' }}
                            autoplay={{ delay: 4000, disableOnInteraction: false }}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1200: { slidesPerView: 3 } }}
                            className="cs-cl-swiper pb-5"
                        >
                            {caseStudies.map((cs, i) => (
                                <SwiperSlide key={i}>
                                    <motion.div
                                        style={{
                                            borderRadius: '14px', overflow: 'hidden',
                                            background: '#fff', border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                                        }}
                                        whileHover={{ y: -6, boxShadow: '0 20px 50px rgba(0,0,0,0.12)' }}
                                    >
                                        <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                                            <img src={cs.image} alt={cs.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <div style={{
                                                position: 'absolute', inset: 0,
                                                background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6) 100%)'
                                            }} />
                                            {cs.dark && (
                                                <div style={{
                                                    position: 'absolute', bottom: '16px', left: '16px', right: '16px',
                                                    color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.4
                                                }}>
                                                    Discover how we transformed video collaboration at scale
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '24px' }}>
                                            <span style={{
                                                fontSize: '0.75rem', fontWeight: 700, color: '#2563eb',
                                                textTransform: 'uppercase', letterSpacing: '1px',
                                                display: 'block', marginBottom: '10px'
                                            }}>+ {cs.tag}</span>
                                            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.5, marginBottom: '20px' }}>{cs.title}</h4>
                                            <Link to="/case-study" style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                background: '#fff', color: '#2563eb',
                                                border: '1.5px solid #2563eb', padding: '9px 20px',
                                                borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                                                textDecoration: 'none', transition: 'background 0.2s, color 0.2s'
                                            }}
                                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2563eb'; }}
                                            >
                                                Read More
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </Link>
                                        </div>
                                    </motion.div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
                            <button className="cs-cl-prev-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#475569', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <div className="cs-cl-pagination" style={{ display: 'flex', gap: '6px' }} />
                            <button className="cs-cl-next-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #2563eb', background: '#2563eb', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.borderColor = '#1d4ed8'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    </div>
                </div>

                <style>{`
                    .cs-cl-swiper .swiper-pagination-bullet {
                        width: 8px; height: 8px; background: #cbd5e1; opacity: 1;
                        border-radius: 50%; margin: 0 4px; transition: all 0.3s;
                    }
                    .cs-cl-swiper .swiper-pagination-bullet-active {
                        background: #2563eb; width: 24px; border-radius: 4px;
                    }
                `}</style>
            </section>

            {/* ── FAQ SECTION ── */}
            <section style={{ padding: '100px 0', background: '#0a0f1e', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.08) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <p style={{
                            color: '#60a5fa', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            FREQUENTLY ASKED QUESTIONS
                        </p>
                        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: '16px' }}>
                            What Would You Like To Know<br />About Collaboration Services
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem' }}>
                            Everything you need to know about our services and solutions
                        </p>
                    </div>

                    <div className="row g-5 align-items-start">
                        {/* Left: Accordion */}
                        <div className="col-lg-7">
                            {faqs.map((faq, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    viewport={{ once: true }}
                                    style={{ marginBottom: '12px' }}
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        style={{
                                            width: '100%', textAlign: 'left',
                                            background: openFaq === i ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                                            border: openFaq === i ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: openFaq === i ? '12px 12px 0 0' : '12px',
                                            padding: '20px 24px',
                                            display: 'flex', alignItems: 'center', gap: '16px',
                                            cursor: 'pointer', transition: 'all 0.25s'
                                        }}
                                    >
                                        <span style={{
                                            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                                            background: openFaq === i ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : 'rgba(255,255,255,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.78rem', fontWeight: 700,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.25s'
                                        }}>
                                            {i + 1}
                                        </span>
                                        <span style={{
                                            flex: 1, fontSize: '0.98rem', fontWeight: 600,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.75)',
                                            lineHeight: 1.45, textAlign: 'left'
                                        }}>
                                            {faq.question}
                                        </span>
                                        <span style={{
                                            width: '24px', height: '24px', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: openFaq === i ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                                            fontSize: '1.4rem', lineHeight: 1, fontWeight: 300,
                                            transition: 'color 0.25s'
                                        }}>
                                            {openFaq === i ? '−' : '+'}
                                        </span>
                                    </button>

                                    {openFaq === i && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: 'rgba(99,102,241,0.07)',
                                                border: '1px solid rgba(99,102,241,0.4)',
                                                borderTop: 'none',
                                                borderRadius: '0 0 12px 12px',
                                                padding: '20px 24px 24px 72px'
                                            }}
                                        >
                                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                                                {faq.answer}
                                            </p>
                                        </motion.div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        {/* Right: CTA card */}
                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', position: 'relative',
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)',
                                    padding: '52px 40px', textAlign: 'center',
                                    minHeight: '360px', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '30px', left: '-20px', width: '90px', height: '90px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', top: '50px', left: '20px', width: '55px', height: '55px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '-20px', right: '40px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '28px', position: 'relative', zIndex: 1
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(1.4rem, 2.5vw, 1.75rem)',
                                    lineHeight: 1.3, marginBottom: '16px', position: 'relative', zIndex: 1
                                }}>
                                    Didn't Find What You Were Looking For?
                                </h3>
                                <p style={{
                                    color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem',
                                    lineHeight: 1.7, marginBottom: '32px',
                                    position: 'relative', zIndex: 1, maxWidth: '280px'
                                }}>
                                    Our team of experts is ready to help you with any questions or custom requirements you may have.
                                </p>
                                <Link to="/contact" style={{ textDecoration: 'none', position: 'relative', zIndex: 1 }}>
                                    <button style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                                        background: '#fff', color: '#4f46e5',
                                        border: 'none', padding: '14px 28px',
                                        borderRadius: '50px', fontWeight: 700, fontSize: '0.92rem',
                                        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.3)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                                    >
                                        Get In Touch With Our Experts
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12h14M12 5l7 7-7 7" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default CollaborationServices;
